import { useState, useEffect } from "react";
import { T } from "../../config/theme.js";
import { Btn, Input } from "../ui/index.jsx";
import { api, trackEvent } from "../../hooks/useApi.js";
import StockSearch from "../pages/StockSearch.jsx";
import { Shield, Scale, Flame, CheckCircle, AlertCircle } from "lucide-react";

// ═══════════════════════════════════════════
// 온보딩 플로우 (Sprint 16-3 최종)
// 3단계: 투자 스타일 → 첫 종목 등록 → 완료
// ═══════════════════════════════════════════

const TOTAL_STEPS = 3;

const STYLES = [
  { key: "stable", icon: Shield, label: "안정적으로 지키는 편입니다", desc: "원금 보전이 최우선" },
  { key: "balanced", icon: Scale, label: "균형 있게 보는 편입니다", desc: "수익과 안정의 타협" },
  { key: "aggressive", icon: Flame, label: "기회를 적극적으로 보는 편입니다", desc: "리스크 감수, 고수익 기대" },
];

function CardSelect({ options, value, onChange }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {options.map(opt => {
        const selected = value === opt.key;
        const Icon = opt.icon;
        return (
          <div key={opt.key} onClick={() => onChange(opt.key)}
            style={{
              padding: "16px 20px", borderRadius: 12,
              border: `1px solid ${selected ? T.accent : T.border}`,
              background: selected ? T.accent + "08" : T.bg,
              cursor: "pointer", transition: "all 0.2s ease",
              display: "flex", alignItems: "center", gap: 16,
            }}>
            <Icon size={24} color={selected ? T.accent : T.textMuted} strokeWidth={selected ? 2 : 1.5} />
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: selected ? T.accent : T.text, marginBottom: 4 }}>{opt.label}</div>
              <div style={{ fontSize: 12, color: T.textMuted }}>{opt.desc}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function OnboardingGoal({ token, onComplete }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [style, setStyle] = useState(null);
  const [holdingForm, setHoldingForm] = useState({ symbol: "", name: "", avg_price: "", qty: "" });
  const [agreed1, setAgreed1] = useState(false);
  const [agreed2, setAgreed2] = useState(false);

  useEffect(() => { setErrorMsg(""); }, [step, style, holdingForm]);

  const saveAndComplete = async () => {
    setSaving(true);
    setErrorMsg("");

    try {
      await api("/auth/onboarding", {
        method: "POST",
        body: JSON.stringify({
          investment_experience: "beginner",
          investment_style: style || "balanced",
          investment_volume: null,
          holding_count: null,
          investment_purpose: null,
          age_range: null,
          pain_points: [],
        }),
      }, token);
    } catch (e) {
      console.error("[ONBOARDING] 설정 저장 실패:", e.message);
      setErrorMsg("설정 저장에 실패했습니다. 잠시 후 다시 시도해주세요.");
      setSaving(false);
      return;
    }

    if (holdingForm.symbol && holdingForm.avg_price && holdingForm.qty) {
      try {
        await api("/holdings", {
          method: "POST",
          body: JSON.stringify({
            symbol: holdingForm.symbol,
            name: holdingForm.name,
            avg_price: +holdingForm.avg_price,
            qty: +holdingForm.qty,
          }),
        }, token);
        trackEvent?.("holding.add", { symbol: holdingForm.symbol, context: "onboarding" });
      } catch (e) {
        console.error("[ONBOARDING] 종목 등록 실패:", e.message);
      }
    }

    setSaving(false);
    onComplete();
  };

  const hasAnyInput = holdingForm.symbol || holdingForm.avg_price || holdingForm.qty;
  const isCompleteInput = holdingForm.symbol && holdingForm.avg_price && holdingForm.qty;

  const handleNextClick = () => {
    if (step === 1 && hasAnyInput && !isCompleteInput) {
      setErrorMsg("종목 정보 입력을 마저 완료해주세요.");
      return;
    }
    if (step < TOTAL_STEPS - 1) setStep(step + 1);
    else saveAndComplete();
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 10000,
      background: "rgba(5, 5, 10, 0.9)", backdropFilter: "blur(12px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20,
    }}>
      <div style={{
        background: T.card, borderRadius: 20, border: `1px solid ${T.border}`,
        boxShadow: "0 24px 48px rgba(0,0,0,0.4)",
        maxWidth: 480, width: "100%", padding: "36px 28px",
        maxHeight: "90vh", overflowY: "auto",
      }}>
        {/* 진행률 바 */}
        <div style={{ display: "flex", gap: 6, marginBottom: 32 }}>
          {Array.from({ length: TOTAL_STEPS }, (_, i) => (
            <div key={i} style={{
              flex: 1, height: 3, borderRadius: 2,
              background: i <= step ? T.accent : T.border,
              transition: "background 0.4s ease",
            }} />
          ))}
        </div>

        {/* ─── Step 1: 투자 성향 ─── */}
        {step === 0 && (
          <div>
            <div className="mono-num label-micro" style={{ color: T.accent, marginBottom: 12 }}>STEP 1</div>
            <h3 style={{ fontSize: 22, fontWeight: 700, color: T.text, marginBottom: 8 }}>
              평소 투자 판단 스타일을 알려주세요
            </h3>
            <p style={{ fontSize: 14, color: T.textDim, marginBottom: 28, lineHeight: 1.6 }}>
              선택하신 성향에 맞춰 분석 기준을 조정합니다.
            </p>
            <CardSelect options={STYLES} value={style} onChange={setStyle} />
          </div>
        )}

        {/* ─── Step 2: 첫 종목 등록 ─── */}
        {step === 1 && (
          <div>
            <div className="mono-num label-micro" style={{ color: T.accent, marginBottom: 12 }}>STEP 2</div>
            <h3 style={{ fontSize: 22, fontWeight: 700, color: T.text, marginBottom: 8 }}>
              분석할 첫 번째 종목을 등록합니다
            </h3>
            <p style={{ fontSize: 14, color: T.textDim, marginBottom: 28, lineHeight: 1.6 }}>
              종목을 등록하면 바로 맞춤 분석을 시작할 수 있습니다.
              <br />지금은 건너뛰고 나중에 등록할 수도 있습니다.
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <div className="label-micro" style={{ marginBottom: 8 }}>종목 검색</div>
                <StockSearch onSelect={(code, name) => setHoldingForm({ ...holdingForm, symbol: code, name })} />
                {holdingForm.symbol && (
                  <div style={{ fontSize: 12, color: T.accent, marginTop: 8, fontWeight: 600 }}>
                    선택됨: {holdingForm.name} <span className="mono-num">({holdingForm.symbol})</span>
                  </div>
                )}
              </div>
              <div>
                <div className="label-micro" style={{ marginBottom: 8 }}>평균 매수가</div>
                <Input placeholder="예: 70000" type="number" className="mono-num" value={holdingForm.avg_price} onChange={e => setHoldingForm({ ...holdingForm, avg_price: e.target.value })} />
              </div>
              <div>
                <div className="label-micro" style={{ marginBottom: 8 }}>보유 수량</div>
                <Input placeholder="예: 10" type="number" className="mono-num" value={holdingForm.qty} onChange={e => setHoldingForm({ ...holdingForm, qty: e.target.value })} />
              </div>
            </div>
          </div>
        )}

        {/* ─── Step 3: 완료 ─── */}
        {step === 2 && (
          <div style={{ textAlign: "center", padding: "32px 0 16px" }}>
            <CheckCircle size={56} color={T.accent} strokeWidth={1.5} style={{ marginBottom: 24 }} />
            <h3 style={{ fontSize: 24, fontWeight: 700, color: T.text, marginBottom: 12 }}>
              준비가 완료되었습니다
            </h3>
            <p style={{ fontSize: 15, color: T.textDim, lineHeight: 1.7, marginBottom: 24 }}>
              입력한 정보를 바탕으로 첫 화면을 구성합니다.
              <br />이제 대시보드에서 데이터 상태를 확인해 보세요.
            </p>
            <div style={{ textAlign: "left", padding: "20px", background: T.bg, borderRadius: 12, border: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 12 }}>서비스 이용 동의</div>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", marginBottom: 10 }}>
                <input type="checkbox" checked={agreed1} onChange={e => setAgreed1(e.target.checked)} style={{ marginTop: 3, accentColor: T.accent }} />
                <span style={{ fontSize: 13, color: T.textDim, lineHeight: 1.6 }}>본 서비스가 투자자문이 아니며, 매매 판단을 제공하지 않음을 이해했습니다</span>
              </label>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", marginBottom: 10 }}>
                <input type="checkbox" checked={agreed2} onChange={e => setAgreed2(e.target.checked)} style={{ marginTop: 3, accentColor: T.accent }} />
                <span style={{ fontSize: 13, color: T.textDim, lineHeight: 1.6 }}>투자 판단과 결과에 대한 책임은 본인에게 있음을 이해했습니다</span>
              </label>
            </div>
          </div>
        )}

        {/* ─── 인라인 에러 ─── */}
        {errorMsg && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 24, color: T.red, fontSize: 13, fontWeight: 600 }}>
            <AlertCircle size={16} /> {errorMsg}
          </div>
        )}

        {/* ─── 버튼 ─── */}
        <div style={{ display: "flex", gap: 10, marginTop: errorMsg ? 12 : 36 }}>
          {step > 0 && (
            <Btn variant="ghost" style={{ flex: 1, justifyContent: "center", padding: "14px" }}
              onClick={() => setStep(step - 1)} disabled={saving}>
              이전
            </Btn>
          )}
          <Btn
            variant={step === 1 && !hasAnyInput ? "outline" : "primary"}
            style={{
              flex: step === 0 ? 1 : 2,
              justifyContent: "center", padding: "14px",
              opacity: (step === 0 && !style) || (step === 2 && (!agreed1 || !agreed2)) ? 0.4 : 1,
              pointerEvents: (step === 0 && !style) || (step === 2 && (!agreed1 || !agreed2)) ? "none" : "auto",
            }}
            onClick={handleNextClick}
            disabled={saving}
          >
            {saving ? "설정 저장 중..."
              : step === 0 ? "다음 단계"
              : step === 1 ? (
                  !hasAnyInput ? "종목 등록 없이 계속하기"
                  : isCompleteInput ? "등록하고 다음으로"
                  : "입력을 마저 완료해주세요"
                )
              : "대시보드로 이동"}
          </Btn>
        </div>
      </div>
    </div>
  );
}
